"use client";
import { useState, useEffect } from "react";
function AddRulesLogic({ onCancel, onSave, editData }) {
  const [ruleType, setRuleType] = useState("required");
  const [errors, setErrors] = useState({});

  //Stores rule form data for all rule types.
  const [formData, setFormData] = useState({
    type: "required",
    requiredMessage: "",
    regexMessage: "",
    compareMessage: "",
    lengthMessage: "",
    pattern: "",
    compare_field: "",
    operator: "",
    min: "",
    max: "",
    dictionary_id: ""
  });

  // Trims and collapses multiple spaces into single space
  const normalizeValue = (value = "") =>
    value.trim().replace(/\s+/g, " ");

  //Prefills rule form data when editing an existing rule
  useEffect(() => {
    if (editData) {
      setRuleType(editData.type || "required");

      setFormData({
        type: editData.type || "required",
        requiredMessage: editData.type === "required" ? editData.message || "" : "",
        regexMessage: editData.type === "regex" ? editData.message || "" : "",
        compareMessage: editData.type === "compare" ? editData.message || "" : "",
        lengthMessage: editData.type === "length" ? editData.message || "" : "",
        pattern: editData.pattern || "",
        compare_field: editData.compare_field || "",
        operator: editData.operator || "",
        min: editData.min !== undefined ? String(editData.min) : "",
        max: editData.max !== undefined ? String(editData.max) : "",
        dictionary_id: editData.dictionary_id || ""
      });
    }
  }, [editData]);

  // Handles rule form input changes with numeric validation
  const handleChange = (e) => {
    const { name, value } = e.target;

    if ((name === "min" || name === "max") && !/^\d*$/.test(value)) {
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: value.trim() ? undefined : prev[name]
    }));
  };

  //Handles rule type change and resets form fields
  const handleRuleTypeChange = (value) => {
    setRuleType(value);
    setErrors((prev) => ({ ...prev, ruleType: "" }));

    if (!editData) {
      setFormData({
        type: value,
        requiredMessage: "",
        regexMessage: "",
        compareMessage: "",
        lengthMessage: "",
        pattern: "",
        compare_field: "",
        operator: "",
        min: "",
        max: "",
        dictionary_id: ""
      });
    } else {
      setFormData((prev) => ({ ...prev, type: value }));
    }
  };

  //Validates rule form fields based on selected rule type
  const validateRuleForm = (data) => {
    const newErrors = {};
    if (!ruleType?.trim()) newErrors.ruleType = "Rule Type is required";

    switch (ruleType) {
      case "required":
        if (!data.requiredMessage?.trim()) newErrors.requiredMessage = "Message is required";
        break;
      case "regex":
        if (!data.pattern?.trim()) newErrors.pattern = "Pattern is required";
        if (!data.regexMessage?.trim()) newErrors.regexMessage = "Message is required";
        break;
      case "compare":
        if (!data.compare_field?.trim()) newErrors.compare_field = "Compare Field is required";
        if (!data.operator?.trim()) newErrors.operator = "Operator is required";
        if (!data.compareMessage?.trim()) newErrors.compareMessage = "Message is required";
        break;
      case "length":
        if (!data.min?.trim()) newErrors.min = "Min value is required";
        else if (!/^\d+$/.test(data.min)) newErrors.min = "Min value must be a number";

        if (!data.max?.trim()) newErrors.max = "Max value is required";
        else if (!/^\d+$/.test(data.max)) newErrors.max = "Max value must be a number";

        if (!data.lengthMessage?.trim()) newErrors.lengthMessage = "Message is required";

        if (!newErrors.min && !newErrors.max) {
          if (Number(data.min) > Number(data.max)) {
            newErrors.min = "Min cannot be greater than Max";
            newErrors.max = "Max must be greater than or equal to Min";
          }
        }
        break;
      default:
        break;
    }

    return newErrors;
  };
  
  //Validate and Submit
  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateRuleForm(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    let ruleObj = { type: ruleType };

    switch (ruleType) {
      case "required":
        ruleObj.message = normalizeValue(formData.requiredMessage);
        break;
      case "regex":
        ruleObj.pattern = normalizeValue(formData.pattern);
        ruleObj.message = normalizeValue(formData.regexMessage);
        break;
      case "compare":
        ruleObj.compare_field = normalizeValue(formData.compare_field);
        ruleObj.operator = normalizeValue(formData.operator);
        ruleObj.message = normalizeValue(formData.compareMessage);
        break;
      case "length":
        ruleObj.min =
          formData.min !== "" && !isNaN(formData.min)
            ? Number(formData.min)
            : formData.min;
        ruleObj.max =
          formData.max !== "" && !isNaN(formData.max)
            ? Number(formData.max)
            : formData.max;
        ruleObj.message = normalizeValue(formData.lengthMessage);
        break;
      case "dictionary_match":
        ruleObj.message = normalizeValue(formData.dictionary_id);
        break;
      default:
        break;
    }

    // Removes undefined keys
    Object.keys(ruleObj).forEach((key) => {
      if (ruleObj[key] === undefined) delete ruleObj[key];
    });

    if (onSave) onSave(ruleObj);
  };


  return { ruleType, formData, onCancel, errors, handleChange, handleRuleTypeChange, handleSubmit };
}

export default AddRulesLogic;
